import type { CSSProperties, ReactNode } from 'react';

import type { JSONContent } from '@tiptap/core';

import type { IconTypes } from './icon/Icon.type';
import type { NavbarV2SerializableNode } from './navbarElementV2/navbarMenuElement/NavbarMenu.type';
// TODO: Use something from tiptap?
export type BasicNode = {
  type: string;
};

// TODO: use something from tiptap/core
export type BasicText = {
  type?: 'text';
  text: string;
  renderAsHtml?: boolean;
};

export type HardBreak = {
  type: 'hardBreak';
};

// TODO: add link element
export type InlineNode = BasicText | HardBreak;

// TODO: use something from tiptap/core
export type Path = Array<number>;

// TODO: use something from tiptap/core
export type NodeEntry<T extends BasicNode | BasicText> = [T, Path];

export type TIconEmoji = {
  type: 'emoji';
  content: string;
  position: 'left' | 'right';
};

export type TIcon =
  | {
      type: 'icon';
      iconType: 'phosphor';
      iconName: IconTypes;
    }
  | TIconEmoji;

export type EmptyChildren = [{ id: string; text: '' }];

export type DreamRenderElementProps<T extends BasicNode> = {
  translations?: Record<string, string>;
  children: React.ReactNode;
  element: T;
  attributes?: {
    [key: `data-${string}`]: any;
  };
  as?: React.ElementType;
  className?: string;
  [key: string]: any;
  isLive?: boolean;
  isInEditor?: boolean;
  autoScroll?: boolean;
  editorStyle?: CSSProperties;
};

export type DreamRenderLeafProps<T extends BasicText> = {
  leaf: T;
  attributes?: {
    [key: `data-${string}`]: any;
  };
  className?: string;
  children?: any;
};

export type AlignType = 'left' | 'center' | 'right' | 'justify' | 'column_dynamic';

export type JustifyType = 'start' | 'center' | 'end';

export type VerticalAlignType =
  | 'align_start'
  | 'align_center'
  | 'align_end'
  | 'space_between'
  | 'space_around'
  | 'space_evenly';

export type CustomAttribute = {
  id?: string;
  className?: string;
  [key: string]: unknown;
};

export type BlockAnimationType = 'fade_in' | 'slide_in' | 'scale_in';

// Animation
export type BlockAnimationFadeIn = {
  type: 'fade_in';
};

export type BlockAnimationSlideIn = {
  type: 'slide_in';
  direction: 'left' | 'right' | 'up' | 'down';
};

export type BlockAnimationScaleIn = {
  type: 'scale_in';
  scaleTo: 'grow' | 'shrink';
};

export type BlockAnimation = BlockAnimationFadeIn | BlockAnimationSlideIn | BlockAnimationScaleIn;

export type LeafAnimationType = 'scroll_words';

export type LeafAnimationScrollWords = {
  type: 'scroll_words';
  words: string[];
};
export type LeafAnimation = LeafAnimationScrollWords;

// *****************************************
// START OF FONT TYPES
// *****************************************
export interface GoogleFontData {
  family: string;
  variants: string[];
  subsets: string[];
  version: string;
  lastModified: string;
  files: Record<string, string>;
  category: string;
  kind: string;
  menu: string;
}

export interface GoogleFontMap {
  [key: string]: {
    family: string;
    variants: string[];
    subsets: string[];
    version: string;
    lastModified: string;
    files: Record<string, string>;
    category: string;
    kind: string;
    menu: string;
  };
}

export interface GoogleFontResponse {
  kind: string;
  items: GoogleFontData[];
}

export interface FontStyleMap {
  [key: string]: { title: string; label?: string; styles: React.CSSProperties };
}

export type FontSelectionMap = Record<string, string>;

export type FontWeightSelectionMap = Record<string, number>;
// *****************************************
// END OF FONT TYPES
// *****************************************

export interface NavbarDocument {
  type: 'doc';
  attrs: {
    data?: {
      mobile?: NavbarV2SerializableNode;
      desktop?: NavbarV2SerializableNode;
    };
  };
  /** Visual navbars use `navbar_menu` under attrs.data; HTML mode stores a page-like doc instead. */
  content: [NavbarV2SerializableNode] | [];
}
export type NavbarItemType = 'link' | 'button' | 'dropdown' | 'search' | 'logo' | 'user_button';
export type NavbarDropdownStyleType = 'full' | 'card';
export type NavbarDropdownItemStyleType = 'link' | 'description' | 'icon' | 'featured';
export type NavbarUserButtonStyleType = 'button' | 'dropdown';

// ****************************************************
// START OF PRICING and PAID-TIER ELEMENT SHARED TYPES
// ****************************************************
export type PremiumOffer = {
  id: string;
  card_required: boolean;
  description: string;
  expires_at: string | null;
  name: string;
  pct_discount: number | null;
  terms: string;
  custom_terms: string | null;
  trial_period_days: number | null;
  discounted_benefit?: string;
  tier_id?: string;
  offer_type?: string;
  paid_trial_price_cents?: number | null;
  paid_trial_billing_cadence?: string | null;
  paid_trial_length?: number | null;
  paid_trial_unit?: string | null;
  paid_trial_inline_suffix?: string | null;
  paid_trial_after_text?: string | null;
};

export interface TierData {
  name: string;
  id: string;
}

export enum PriceInterval {
  MONTH = 'month',
  QUARTER = 'quarter',
  YEAR = 'year',
  ONE_TIME = 'one_time',
  DONATION = 'donation',
}

export interface Price {
  id: string;
  amount_cents: number;
  original_amount_cents: number;
  formatted_amount?: string;
  currency: string;
  interval: PriceInterval;
  cta: string;
  denominator?: string;
  features: string[];
  premium_offer?: PremiumOffer;
}

export enum TierStatus {
  ACTIVE = 'active',
  ARCHIVED = 'archived',
}

export interface Tier {
  id: string;
  publication_id: string;
  name: string;
  prices: Price[];
  status: TierStatus;
  description?: string;
  redirect_url?: string;
  gifting_enabled?: boolean;
  group_enabled?: boolean;
  group_minimum_quantity?: number;
  group_discount_percent?: number;
  enabled: boolean;
  should_split_prices?: boolean;
  cta?: string;
  discord_access: boolean;
}

export type StripeSessionIds = Record<string, string>;

export type StripeCardElementComponent = React.FunctionComponent<{ className?: string; options?: any }>;
export type StripePaymentElementComponent = React.FunctionComponent<{ options?: any }>;
export type CardlessPremiumOfferSignupElementComponent = React.FunctionComponent;
export type PaymentRequestButtonElementComponent = ReactNode;
export type PricingFormMode = 'gift' | 'individual' | 'group';

// ****************************************************
// START OF RECOMMENDATION ELEMENT SHARED TYPES
// ****************************************************

export interface Recommendation {
  id: string;
  impression_token?: string;
  slot_position?: number;
  name: string;
  publisher_name?: string | null;
  description: string | null;
  logo_url: string;
  site_url: string;
  sponsored: boolean;
  recommended_publication_id: string;
  isOwner: boolean;
  // Set when this card recommends one of the current publication's own newsletter
  // lists (the post-signup opt-in). Drives the form field the widget submits.
  newsletter_list_id?: string;
  badge?: string;
}

// ****************************************************
// END OF RECOMMENDATION ELEMENT SHARED TYPES
// ****************************************************

// ****************************************************
// START OF POST ELEMENT SHARED TYPES
// ****************************************************

interface ProfilePicture {
  landscape: URL;
  thumb: URL;
  url: string;
}

export interface Author {
  id: string;
  slug?: string;
  name: string;
  bio: string;
  profile_picture: ProfilePicture;
  twitter_handle?: string;
  facebook_url?: string;
  instagram_url?: string;
  linkedin_url?: string;
  tiktok_url?: string;
  youtube_url?: string;
  discord_url?: string;
  threads_url?: string;
  bluesky_url?: string;
  rss_url?: string;
}

export type ContentTag = {
  id: string;
  display: string;
  slug?: string;
};

export interface PostData {
  id: string;
  web_title: string;
  web_subtitle?: string;
  comments_state: string;
  override_scheduled_at: string;
  scheduled_at?: string;
  /** @deprecated use is_premium instead (Never Remove, some older tip-tap content still uses this */
  audience: 'premium' | 'free' | 'both';
  is_premium: boolean;
  slug: string;
  content_tags: ContentTag[];
  image_url: string;
  authors: Author[];
  estimated_reading_time: number | null;
  estimated_reading_time_display: string | null;
}

export interface PostInsightsData {
  likes: number;
  comments: number;
  isLiked?: boolean;
}

export type PostCardSchema = {
  node: string;
  className: string;
  children: PostCardSchema[];
};

export type PostCommentsData = {
  commentsCount?: number;
};

export type PostLikesData = {
  isLiked?: boolean;
  likesCount?: number;
};

// ****************************************************
// END OF POST ELEMENT SHARED TYPES
// ****************************************************

interface URL {
  url: string;
}
export interface Pagination {
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
}

// ****************************************************
// START OF CAROUSEL/SLIDER ELEMENT SHARED TYPES
// ****************************************************

export type SliderControlsDisplayType = 'inline' | 'overlay';

export type CarouselCompatibleAttributes = {
  // Desktop Slider (Embla)
  isViewportMobile?: boolean;
  columns?: string;
  shouldScrollHorizontally?: boolean;
  sliderControlsDisplayType?: SliderControlsDisplayType;
  sliderControlsTextColor?: CSSProperties['color'];
  sliderControlsBackgroundColor?: CSSProperties['backgroundColor'];
  sliderControlsBorderColor?: CSSProperties['borderColor'];

  // Mobile Slider (Embla)
  mobileShouldScrollHorizontally?: boolean;
  mobileSliderControlsDisplayType?: SliderControlsDisplayType;
  mobileSliderControlsTextColor?: CSSProperties['color'];
  mobileSliderControlsBackgroundColor?: CSSProperties['backgroundColor'];
  mobileSliderControlsBorderColor?: CSSProperties['borderColor'];
};

// ****************************************************
// END OF CAROUSEL/SLIDER SHARED TYPES
// ****************************************************

// ****************************************************
// START OF PAYWALL ELEMENT SHARED TYPES
// ****************************************************

export interface IPaywallStyle {
  show_image: boolean;
  background_color: string;
  text_color: string;
  button_background_color: string;
  button_text_color: string;
  container_shadow: string;
}

export interface Paywall {
  id: string;
  name: string;
  title: string;
  description: string;
  cta_text: string;
  subheader: string;
  benefits: string[];
  website_default: boolean;
  offer_id?: string;
  theme: PostTheme;
  publication_id: string;
  style: IPaywallStyle;
  created_at: string;
  updated_at: string;
  image_url: string;
}

// ****************************************************
// END OF PAYWALL ELEMENT SHARED TYPES
// ****************************************************

// ****************************************************
// START OF POST CONTENT ELEMENT SHARED TYPES
// ****************************************************

export type TiptapState = { [key: string]: any };

export enum PostThemeLineHeights {
  Less = 'Less',
  Normal = 'Normal',
  More = 'More',
}

export type PostThemeLineHeight = keyof typeof PostThemeLineHeights;

export interface PostTheme {
  quote_line_height_size?: PostThemeLineHeight;
  quote_2_line_height_size?: PostThemeLineHeight;
  quote_3_line_height_size?: PostThemeLineHeight;
  paragraph_line_height_size?: PostThemeLineHeight;
  h1_line_height_size?: PostThemeLineHeight;
  h2_line_height_size?: PostThemeLineHeight;
  h3_line_height_size?: PostThemeLineHeight;
  h4_line_height_size?: PostThemeLineHeight;
  h5_line_height_size?: PostThemeLineHeight;
  h6_line_height_size?: PostThemeLineHeight;
  list_line_height_size?: PostThemeLineHeight;
  padding?: string;
  padding_top?: string;
  padding_bottom?: string;
  padding_left?: string;
  padding_right?: string;
  padding_right_value_w_fallback?: string;
  padding_left_value_w_fallback?: string;
  margin_left?: string;
  margin_right?: string;
  margin_top?: string;
  margin_bottom?: string;
  border_width?: string;
  border_radius?: number;
  border_color?: string;
  background_color?: string;
  content_background_color?: string;
  max_width?: string;
  paragraph_font_size?: string;
  paragraph_font_family?: string;
  paragraph_padding_top?: string;
  paragraph_padding_bottom?: string;
  paragraph_color?: string;
  inline_link_color?: string;
  inline_link_italic?: string;
  inline_link_bold?: string;
  inline_link_underline?: string;
  inline_link_underline_color?: string;
  h1_font_size?: string;
  h1_font_family?: string;
  h1_font_color?: string;
  h1_font_weight?: string;
  h1_padding_top?: string;
  h1_padding_bottom?: string;
  h2_font_size?: string;
  h2_font_family?: string;
  h2_font_color?: string;
  h2_font_weight?: string;
  h2_padding_top?: string;
  h2_padding_bottom?: string;
  h3_font_size?: string;
  h3_font_family?: string;
  h3_font_color?: string;
  h3_font_weight?: string;
  h3_padding_top?: string;
  h3_padding_bottom?: string;
  h4_font_size?: string;
  h4_font_family?: string;
  h4_font_color?: string;
  h4_font_weight?: string;
  h4_padding_top?: string;
  h4_padding_bottom?: string;
  h5_font_size?: string;
  h5_font_family?: string;
  h5_font_color?: string;
  h5_font_weight?: string;
  h5_padding_top?: string;
  h5_padding_bottom?: string;
  h6_font_size?: string;
  h6_font_family?: string;
  h6_font_color?: string;
  h6_font_weight?: string;
  h6_padding_top?: string;
  h6_padding_bottom?: string;
  list_font_size?: string;
  list_font_family?: string;
  list_padding_left?: string;
  list_padding_right?: string;
  list_padding_top?: string;
  list_padding_bottom?: string;
  ordered_list_style_type?: string;
  list_item_spacing?: string;
  list_font_color?: string;
  quote_font_size?: string;
  quote_font_family?: string;

  quote_background_color?: string;
  quote_font_color?: string;
  quote_alignment?: string;
  quote_border_color?: string;
  quote_border_width?: string;
  quote_border_radius?: string;
  quote_byline_color?: string;
  quote_byline_alignment?: string;
  quote_byline_spacing?: string;
  quote_byline_font_size?: string;
  quote_byline_font_family?: string;
  quote_byline_padding_top?: string;
  quote_byline_padding_bottom?: string;
  quote_padding_left?: string;
  quote_padding_right?: string;
  quote_padding_top?: string;
  quote_padding_bottom?: string;
  quote_margin_left?: string;
  quote_margin_right?: string;
  quote_margin_top?: string;
  quote_margin_bottom?: string;
  quote_2_font_size?: string;
  quote_2_font_family?: string;

  quote_2_background_color?: string;
  quote_2_font_color?: string;
  quote_2_alignment?: string;
  quote_2_border_color?: string;
  quote_2_border_left_width?: string;
  quote_2_border_radius?: string;
  quote_2_byline_color?: string;
  quote_2_byline_alignment?: string;
  quote_2_byline_spacing?: string;
  quote_2_byline_font_size?: string;
  quote_2_byline_font_family?: string;
  quote_2_byline_padding_top?: string;
  quote_2_byline_padding_bottom?: string;
  quote_2_padding_left?: string;
  quote_2_padding_right?: string;
  quote_2_padding_top?: string;
  quote_2_padding_bottom?: string;
  quote_2_margin_left?: string;
  quote_2_margin_right?: string;
  quote_2_margin_top?: string;
  quote_2_margin_bottom?: string;
  quote_3_font_size?: string;
  quote_3_font_family?: string;

  quote_3_background_color?: string;
  quote_3_font_color?: string;
  quote_3_alignment?: string;
  quote_3_mark_color: string;
  quote_3_mark_alignment: string;
  quote_3_mark_spacing: string;
  quote_3_mark_font_size: string;
  quote_3_mark_font_family: string;
  quote_3_mark_font_weight?: string;
  quote_3_byline_color?: string;
  quote_3_byline_alignment?: string;
  quote_3_byline_spacing?: string;
  quote_3_byline_font_size?: string;
  quote_3_byline_font_family?: string;
  quote_3_byline_padding_top?: string;
  quote_3_byline_padding_bottom?: string;
  quote_3_padding_left?: string;
  quote_3_padding_right?: string;
  quote_3_padding_top?: string;
  quote_3_padding_bottom?: string;
  quote_3_margin_left?: string;
  quote_3_margin_right?: string;
  quote_3_margin_top?: string;
  quote_3_margin_bottom?: string;
  image_padding_top?: string;
  image_padding_bottom?: string;
  image_source_y_spacing?: string;
  image_source_x_spacing?: string;
  image_source_alignment?: string;
  image_source_font_family?: string;
  image_source_style?: string;
  image_source_color?: string;
  image_source_font_size?: string;
  hyperlink_font_family?: string;
  hyperlink_background_color?: string;
  hyperlink_border_color?: string;
  hyperlink_border_width?: string;
  hyperlink_border_radius?: string;
  hyperlink_image_placement?: string;
  hyperlink_title_color?: string;
  hyperlink_title_size?: string;
  hyperlink_hide_title?: string;
  hyperlink_description_color?: string;
  hyperlink_description_size?: string;
  hyperlink_hide_description?: string;
  hyperlink_link_color?: string;
  hyperlink_link_size?: string;
  hyperlink_hide_link?: string;
  hyperlink_padding?: string;
  hyperlink_x_spacing?: string;
  hyperlink_y_spacing?: string;
  button_background_color?: string;
  button_text_color?: string;
  button_border_width?: string;
  button_border_top_width?: string;
  button_border_right_width?: string;
  button_border_bottom_width?: string;
  button_border_left_width?: string;
  button_border_radius?: string;
  button_border_radius_top_left?: string;
  button_border_radius_top_right?: string;
  button_border_radius_bottom_right?: string;
  button_border_radius_bottom_left?: string;
  button_border_color?: string;
  button_padding_top?: string;
  button_padding_bottom?: string;
  button_padding_left?: string;
  button_padding_right?: string;
  button_font_weight?: string;
  button_margin_top?: string;
  button_margin_bottom?: string;
  button_font_family?: string;
  button_font_size?: string;
  // RSS properties
  rss_title_font_family?: string;
  rss_title_font_weight?: string;
  rss_title_line_height?: string;
  rss_title_font_color?: string;
  rss_body_font_family?: string;
  rss_body_font_weight?: string;
  rss_body_line_height?: string;
  rss_body_font_color?: string;
  rss_cta_button_font_color?: string;
  rss_cta_button_background_color?: string;
  rss_cta_button_border_color?: string;
  rss_cta_button_border_radius?: string;
  rss_cta_button_border_width?: string;
  rss_cta_button_border_top_width?: string;
  rss_cta_button_border_right_width?: string;
  rss_cta_button_border_bottom_width?: string;
  rss_cta_button_border_left_width?: string;
  rss_cta_button_border_radius_top_left?: string;
  rss_cta_button_border_radius_top_right?: string;
  rss_cta_button_border_radius_bottom_left?: string;
  rss_cta_button_border_radius_bottom_right?: string;
  rss_cta_button_padding_top?: string;
  rss_cta_button_padding_right?: string;
  rss_cta_button_padding_bottom?: string;
  rss_cta_button_padding_left?: string;
  rss_cta_button_font_family?: string;
  rss_cta_button_font_weight?: string;
  rss_cta_button_font_size?: string;
  rss_cta_link_font_color?: string;
  rss_cta_link_underline_color?: string;
  rss_cta_link_bold?: boolean;
  rss_cta_link_italic?: boolean;
  rss_cta_link_underline?: boolean;
  rss_cta_link_font_size?: string;
  header_top_bar_alignment?: string;
  header_top_date_format?: string;
  header_topline_show_date?: string;
  header_topline_show_read_online?: string;
  header_topline_show_reading_time?: string;
  header_topline_reading_time_font_color?: string;
  header_topline_reading_time_label?: string;
  header_asset_id?: any;
  header_asset_integer_id?: any;
  header_asset_width?: string;
  header_asset_url?: any;
  header_title_font_family?: string;
  header_title_font_size?: string;
  header_title_alignment?: string;
  header_title_font_weight?: string;
  header_title_font_color?: string;
  header_title_vertical_padding?: string;
  header_subtitle_font_family?: string;
  header_subtitle_font_size?: string;
  header_subtitle_alignment?: string;
  header_subtitle_font_weight?: string;
  header_subtitle_font_color?: string;
  header_subtitle_vertical_padding?: string;
  header_byline_font_family?: string;
  header_byline_font_size?: string;
  header_byline_font_weight?: string;
  header_byline_font_color?: string;
  header_byline_accent_color?: string;
  header_byline_vertical_padding?: string;
  header_byline_avatar_size?: string;
  header_byline_date_format?: string;
  header_byline_underline_author?: string;
  header_byline_show_date?: string;
  header_byline_show_read_online?: string;
  header_byline_reading_time_enabled?: string;
  header_byline_reading_time_font_color?: string;
  header_byline_reading_time_label?: string;
  header_custom_css?: string;
  header_padding?: string;
  footer_padding?: string;
  footer_margin?: string;
  footer_background_color?: string;
  footer_font_family?: string;
  footer_font_color?: string;
  footer_top_border_width?: string;
  footer_top_border_color?: string;
  footer_top_border_size?: string;
  footer_social_background_color?: string;
  footer_social_icon_color?: string;
  content_break_y_margin?: string;
  content_break_width?: string;
  content_break_style?: string;
  content_break_color?: string;
  content_break_thickness?: string;
  table_border_width?: string;
  table_border_color?: string;
  table_padding_top?: string;
  table_padding_bottom?: string;
  table_header_font_family?: string;
  table_header_font_color?: string;
  table_header_background_color?: string;
  table_header_padding?: string;
  table_cell_font_family?: string;
  table_cell_font_color?: string;
  table_cell_background_color?: string;
  table_cell_padding?: string;
  name?: any;
  purpose?: string;
  owner_id?: string;
  header_asset?: any;

  theme_version?: number | string;
}

export interface WebTheme {
  background_color: string;
  body_font: string;
  border_color: string;
  border_radius: string;
  border_style: string;
  button_font: string;
  content_theme: string;
  header_font: string;
  logo_shadow: string;
  name: string | null;
  owner_id: string;
  primary_color: string;
  purpose: 'web';
  secondary_color: string;
  subscribe_background_color: string;
  subscribe_theme: 'light' | string;
  tertiary_color: string;
  text_on_background_color: string;
  text_on_primary_color: string;
  text_on_secondary_color: string;
  text_on_subscribe_background_color: string;
  text_on_tertiary_color: string;
}

// ****************************************************
// END OF POST CONTENT ELEMENT SHARED TYPES
// ****************************************************

// ****************************************************
// START OF POST COMMENTS ELEMENT SHARED TYPES
// ****************************************************

export type CommentActionType = 'delete' | 'report' | 'edit';

export interface CommentData {
  id: string;
  content: string;
  created_at: string;
  updated_at: string;
  deleted_by_commenter_at: string;
  removed_at: string;
  subcomments_count: number;
  sub_comments: CommentData[];
  max_allowed_depth_level: number;
  commenter: string;
  depth_level: number;
  commenter_id: number | string;
  avatar_url: string;
  is_moderator_comment: boolean;
  likes: (number | string)[];
  // the value of these properties is evaluated in orchid-v2 -> PostComments component
  // as it is specific to the logged in admin or subscriber
  is_liked: boolean;
  can_delete: boolean;
  can_edit: boolean;
}

// ****************************************************
// END OF POST COMMENTS ELEMENT SHARED TYPES
// ****************************************************

// ****************************************************
// START OF SOCIALS ELEMENT SHARED TYPES
// ****************************************************

export type SocialsData = {
  twitter_url?: string;
  tiktok_url?: string;
  facebook_url?: string;
  instagram_url?: string;
  linkedin_url?: string;
  youtube_url?: string;
  discord_url?: string;
  threads_url?: string;
  bluesky_url?: string;
  rss_url?: string;
};

// ****************************************************
// END OF SOCIALS ELEMENT SHARED TYPES
// ****************************************************

// ****************************************************
// START OF RECOMMENDED POSTS ELEMENT SHARED TYPES
// ****************************************************

export interface RecommendedPostData {
  id: string;
  web_title: string;
  web_subtitle: string;
  slug: string;
  image_url: string;
  audience: string;
  override_scheduled_at: string;
}

// ****************************************************
// END OF RECOMMENDED POSTS ELEMENT SHARED TYPES
// ****************************************************

// ****************************************************
// START OF BREADCRUMBS ELEMENT SHARED TYPES
// ****************************************************

export interface Breadcrumb {
  href?: string;
  text: string;
}

// ****************************************************
// END OF BREADCRUMBS ELEMENT SHARED TYPES
// ****************************************************

// ****************************************************
// START OF POST HEADER ELEMENT SHARED TYPES
// ****************************************************

export interface PostHeaderData {
  id: string;
  web_title: string;
  web_subtitle?: string;
  comments_state: string;
  override_scheduled_at: string;
  scheduled_at?: string;
  /** @deprecated use is_premium instead (Never Remove, some older tip-tap content still uses this */
  audience: 'premium' | 'free' | 'both';
  is_premium: boolean;
  slug: string;
  content_tags: ContentTag[];
  image_url?: string | undefined;
  image_width?: number | null;
  image_height?: number | null;
  thumbnail_binding?: string | null;
  authors: Author[];
  estimated_reading_time: number | null;
  estimated_reading_time_display: string | null;
  display_thumbnail_on_web: boolean;
  display_title_on_web: boolean;
  display_subtitle_on_web: boolean;
  author_urls: Record<string, string>;
}

export type PostHeaderCardStructure = {
  node: string;
  className: string;
  children: PostHeaderCardStructure[];
};

// ****************************************************
// END OF POST HEADER ELEMENT SHARED TYPES
// ****************************************************

// ****************************************************
// START OF PODCAST SHARED TYPES
// ****************************************************

export interface PodcastShow {
  id: string;
  title: string;
  slug: string;
  description: string;
  is_premium?: boolean;
  platform_links: Record<string, string | null>;
}

export type PodcastEpisodeTranscriptLine = {
  start: number;
  end: number;
  text: string;
};

export type PodcastEpisodeData = {
  id: string;
  title: string;
  slug: string;
  description: string;
  truncated_description: string;
  season_and_episode_number_display: string | null;
  explicit: boolean;
  explicit_display?: string;
  /** Whether audio is blocked for the current viewer */
  is_premium: boolean;
  audio_url: string;
  audio_preview_url?: string | null;
  /** Nil when audio is blocked for the viewer or no processed file exists. */
  audio_type?: string | null;
  thumbnail_url?: string;
  waveform?: unknown;
  publish_date: string;
  display_date_formatted: string;
  duration: number;
  audio_duration_display?: string | null;
  show: PodcastShow;
  tiptap_content: JSONContent | null;
  web_url: string;
  description_html: string;
  transcript?: PodcastEpisodeTranscriptLine[] | null;
  // Backwards compatibility for YouTube channel/playlist support for RSS feeds
  // of sources other than beehiiv podcast shows/episodes
  youtube_video_id?: string;
  /** Episode-level listen URLs (Apple, Spotify, YouTube). Distinct from `show.platform_links`; merge in the client for display. */
  platform_links?: Record<string, string | null>;
};

// ****************************************************
// END OF PODCAST SHARED TYPES
// ****************************************************

export type TransitionProps = {
  transitionProperty?: CSSProperties['transition'];
  transitionDelay?: CSSProperties['transitionDelay'];
  transitionDuration?: CSSProperties['transitionDuration'];
  transitionTimingFunction?: CSSProperties['transitionTimingFunction'];
  transitionBehavior?: CSSProperties['transitionBehavior'];
  transitionFinalValue?: string | number;
};

// ****************************************************
// START OF UTM SHARED TYPES
// ****************************************************

export type UTMAttrs = {
  utmParamsEnabled?: boolean;
  utmCampaign?: string;
  utmMedium?: string;
  utmSource?: string;
};

// ****************************************************
// END OF UTM SHARED TYPES
// ****************************************************

// ****************************************************
// START OF RSS FEED ELEMENT SHARED TYPES
// ****************************************************

export interface RSSFeedDataYouTube {
  feed: {
    link: RSSFeedDataYouTubeLink[];
    id: string;
    'yt:channelId': string;
    title: string;
    author: RSSFeedDataYouTubeAuthor;
    published: string;
    entry: RSSFeedDataYouTubeEntry[];
    '@xmlns:yt': string;
    '@xmlns:media': string;
    '@xmlns': string;
  };
}
export interface RSSFeedDataYouTubeLink {
  '@rel': string;
  '@href': string;
}

export interface RSSFeedDataYouTubeAuthor {
  name: string;
  uri: string;
}

export interface RSSFeedDataYouTubeEntry {
  id: string;
  'yt:videoId': string;
  'yt:channelId': string;
  title: string;
  link: RSSFeedDataYouTubeLink;
  author: RSSFeedDataYouTubeAuthor;
  published: string;
  updated: string;
  'media:group': RSSFeedDataYouTubeMediaGroup;
}

export interface RSSFeedDataPodcastEpisodeItem {
  id: string;
  title: string;
  description: string;
  published: string;
  thumbnailUrl: string;
  url: string;
}

export interface RSSFeedDataYouTubeMediaGroup {
  'media:title': string;
  'media:content': MediaContent;
  'media:thumbnail': RSSFeedDataYouTubeMediaThumbnail;
  'media:description'?: string;
  'media:community': RSSFeedDataYouTubeMediaCommunity;
}

export interface MediaContent {
  '@url': string;
  '@type': string;
  '@width': string;
  '@height': string;
}

export interface RSSFeedDataYouTubeMediaThumbnail {
  '@url': string;
  '@width': string;
  '@height': string;
}

export interface RSSFeedDataYouTubeMediaCommunity {
  'media:starRating': RSSFeedDataYouTubeMediaStarRating;
  'media:statistics': RSSFeedDataYouTubeMediaStatistics;
}

export interface RSSFeedDataYouTubeMediaStarRating {
  '@count': string;
  '@average': string;
  '@min': string;
  '@max': string;
}

export interface RSSFeedDataYouTubeMediaStatistics {
  '@views': string;
}

export interface RSSFeedDataPodcast {
  rss: {
    channel: RSSFeedDataPodcastChannel;
    '@xmlns:dc': string;
    '@xmlns:content': string;
    '@xmlns:atom': string;
    '@xmlns:itunes': string;
    '@version': string;
  };
}
export interface RSSFeedDataPodcastChannel {
  title: string;
  description: string;
  link: string;
  generator: string;
  lastBuildDate: string;
  'atom:link': RSSFeedDataPodcastAtomLink;
  author: string;
  copyright: string;
  language: string;
  webMaster: string;
  'itunes:new_feed_url': string;
  'itunes:author': string;
  'itunes:subtitle': string;
  'itunes:type': string;
  'itunes:owner': RSSFeedDataPodcastItunesOwner;
  'itunes:explicit': string;
  'itunes:category': RSSFeedDataPodcastItunesCategory[];
  'itunes:image': RSSFeedDataPodcastItunesImage;
  item: RSSFeedDataPodcastItem[];
  image: {
    url: string;
    title: string;
    link: string;
  };
}

export interface RSSFeedDataPodcastShow {
  episodes: RSSFeedDataPodcastEpisodeItem[];
}

export interface RSSFeedDataPodcastAtomLink {
  '@href': string;
  '@rel': string;
  '@type': string;
}

export interface RSSFeedDataPodcastItunesOwner {
  'itunes:name': string;
  'itunes:email': string;
}

export interface RSSFeedDataPodcastItunesCategory {
  '@text': string;
  'itunes:category'?: RSSFeedDataPodcastItunesCategory2;
}

export interface RSSFeedDataPodcastItunesCategory2 {
  '@text': string;
}

export interface RSSFeedDataPodcastItunesImage {
  '@href': string;
}

export interface RSSFeedDataPodcastItem {
  title: string;
  description: string;
  link: string;
  guid: string;
  'dc:creator': string;
  pubDate: string;
  enclosure: RSSFeedDataPodcastEnclosure;
  'itunes:author': string;
  'itunes:explicit': string;
  'itunes:duration': string;
  'itunes:image': RSSFeedDataPodcastItunesImage2;
}

export interface RSSFeedDataPodcastEnclosure {
  '@url': string;
  '@length': string;
  '@type': string;
}

export interface RSSFeedDataPodcastItunesImage2 {
  '@href': string;
}

export interface RSSFeedDataPodcastAppleResult {
  resultCount: number;
  results: RSSFeedDataPodcastAppleEpisode[];
}

export interface RSSFeedDataPodcastAppleEpisode {
  artistIds: any[];
  artworkUrl600: string;
  artworkUrl160: string;
  episodeFileExtension: string;
  episodeContentType: string;
  collectionViewUrl: string;
  trackViewUrl: string;
  artworkUrl60: string;
  trackTimeMillis: number;
  contentAdvisoryRating: string;
  genres: RSSFeedDataPodcastAppleGenre[];
  episodeGuid: string;
  closedCaptioning: string;
  shortDescription: string;
  trackId: number;
  trackName: string;
  releaseDate: string;
  collectionId: number;
  collectionName: string;
  feedUrl: string;
  kind: string;
  wrapperType: string;
  description: string;
  country: string;
  previewUrl: string;
  episodeUrl: string;
}

export interface RSSFeedDataPodcastAppleGenre {
  name: string;
  id: string;
}

export interface RSSFeedDataPodcastSpotifyEpisode {
  audio_preview_url: string;
  description: string;
  html_description: string;
  duration_ms: number;
  explicit: boolean;
  external_urls: ExternalUrls;
  href: string;
  id: string;
  images: SpotifyImage[];
  is_externally_hosted: boolean;
  is_playable: boolean;
  language: string;
  languages: string[];
  name: string;
  release_date: string;
  release_date_precision: string;
  resume_point: ResumePoint;
  type: string;
  uri: string;
  restrictions: Restrictions;
}

export interface ExternalUrls {
  spotify: string;
}

export interface SpotifyImage {
  url: string;
  height: number;
  width: number;
}

export interface ResumePoint {
  fully_played: boolean;
  resume_position_ms: number;
}

export interface Restrictions {
  reason: string;
}

// ****************************************************
// END OF RSS FEED ELEMENT SHARED TYPES
// ****************************************************

// ****************************************************
// START OF PRODUCTS SHARED TYPES
// ****************************************************

export interface ProductVariantAssetData {
  id: string;
  url: string;
  alt_text?: string;
  position: number;
  is_default: boolean;
}

export interface ProductVariantData {
  id: string;
  name: string;
  stripe_price_id: string;
  unit_amount_cents: number;
  original_unit_amount_cents?: number | null;
  currency: string;
  default_asset_url?: string;
  assets?: ProductVariantAssetData[];
}

export interface ProductReview {
  id: string;
  avatar_url?: string;
  product_id: string;
  subscription_id: string;
  name: string;
  review: string;
  score: number;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface ProductReviewsSummary {
  average_score: number;
  total_reviews_count: number;
  scores_to_counts_map: Record<string, number>;
}

export type ProductData = {
  id: string;
  name: string;
  description: string;
  tiptap_state: JSONContent;
  cta_text: string;
  slug: string;
  url: string;
  image_url?: string;
  product_type?: string;
  free: boolean;
  updated_at?: string;
  created_at?: string;
  product_variants: ProductVariantData[];
  default_variant?: ProductVariantData;
  reviews?: ProductReview[];
  reviews_summary?: ProductReviewsSummary | null;
};

export type OrderFulfillmentStatus = 'completed' | 'pending';
export type FulfillmentStatus = 'pending' | 'completed' | 'cancelled';

export type Fulfillment = {
  id: string;
  status: FulfillmentStatus;
  created_at: string;
  updated_at: string;
  redeemed_at: string | null;
  can_access: boolean;
  metadata: Record<string, any>;
};

export type OrderItemData = {
  id: string;
  free: boolean;
  product_description: string;
  product_slug: string;
  hide_access_fulfillment_button: boolean;
  price: string;
  unit_amount_cents: number;
  currency: string;
  product_variant: {
    id: string;
    name: string;
    type: string;
    thumbnail_url: string;
    unit_amount_cents: number;
    price: string;
    currency: string;
    redeemable_metadata: {
      cal_com_event_slug?: string | null;
      cal_com_event_username?: string | null;
      event_starts_at?: string | null;
      event_ends_at?: string | null;
      time_zone?: string | null;
      time_zone_iana?: string | null;
      location_url?: string | null;
      max_seats?: number | null;
      status?: string | null;
    };
  };
  fulfillment: Fulfillment | null;
  product_review?: ProductReviewData;
};

export type ProductReviewData = {
  id: string;
  product_id: string;
  subscription_id: string;
  name: string;
  review: string;
  score: number;
  status: string;
  created_at: string;
  updated_at: string;
  avatar_url: null;
};

export type OrderData = {
  id: string;
  display_order_number: string;
  order_currency: string;
  buyer_email: string;
  free: boolean;
  order_items: OrderItemData[];
  formatted_total: string;
  total_amount_cents: number;
  original_total_amount_cents: number;
  formatted_original_total: string;
  discount_amount_cents: number;
  formatted_discount: string;
  refund_status?: string;
  refunded_amount_cents: number;
  formatted_refunded_amount: string;
  refund_note?: string;
  fulfillment_status: OrderFulfillmentStatus;
  created_at: string;
  updated_at: string;
  can_view_receipt: boolean;
};

// Redemption Modal
export interface RedemptionModalOrderData {
  id: string;
  order_items: (Pick<OrderItemData, 'id' | 'product_description' | 'product_slug'> & {
    product_variant: {
      id: string;
      name: string;
      type: string;
      thumbnail_url: string;
      event_starts_at?: string;
      event_ends_at?: string;
      time_zone?: string;
      iana_time_zone?: string;
    };
    fulfillment: Pick<Fulfillment, 'id' | 'can_access'>;
  })[];
}

// ****************************************************
// END OF PRODUCTS SHARED TYPES
// ****************************************************

// ****************************************************
// START OF GDPR TRANSLATIONS TYPES
// ****************************************************
export interface GDPRTranslations {
  consentText: string;
  termsOfUseText: string;
  privacyPolicyText: string;
  andText: string;
  consentTextVariantTwo?: string;
  termsOfServiceText?: string;
  chooseNewslettersText?: string;
  selectAllText?: string;
}

// ****************************************************
// END OF GDPR TRANSLATIONS TYPES
// ****************************************************

// ****************************************************

// ****************************************************
// START OF ADDITIONAL FIELDS TYPES
// ****************************************************
export type SubscriberPreferenceOption = {
  id: string;
  value: string;
};

export type Preference = {
  id: string;
  name: string;
  placeholder?: string;
  custom_field_id: string;
  preference_type: string;
  display_type: string;
  required?: boolean;
  subscriber_preference_options: SubscriberPreferenceOption[];
  name_override?: string;
};
// ****************************************************
// END OF ADDITIONAL FIELDS TYPES
// ****************************************************

// ****************************************************
// START OF VIDEO ASSET TYPES
// ****************************************************
// Surface-agnostic wire contracts for the video asset lifecycle (WEB-9748),
// shared by community, the website builder, and podcasts. Keys are snake_case to
// match the Rails responses the frontend consumes directly. Mirrors the backend
// Video model's states.
export type VideoAssetStatus = 'pending' | 'uploading' | 'transcoding' | 'ready' | 'blocked' | 'deleted' | 'failed';

export type VideoSurface = 'community_post' | 'podcast_episode' | 'community_resource' | 'website_page';

// The record a video is bound to, mirroring the polymorphic videoable columns.
export type VideoPlacement = {
  type: string;
  id: string;
};

export type VideoAsset = {
  id: string;
  status: VideoAssetStatus;
  surface: VideoSurface;
  placement: VideoPlacement | null;
  output_profile: string;
  created_at: string;
  updated_at: string;
};

// The upload limits of a surface, in the shape the server sends.
export type VideoUploadLimits = {
  max_bytes: number;
  max_duration_in_ms: number;
};

// Where the browser sends the file bytes. A single PUT for small files; an S3
// multipart session (keyed by upload_id) for large ones, whose part lifecycle is
// served by the multipart host callbacks.
export type VideoAssetDirectUpload =
  | {
      // The browser sends the raw bytes straight to `url` with `headers`, no
      // Swarm/Orchid round trip in between.
      transport: 's3';
      url: string;
      headers: Record<string, string>;
      expires_at: string;
    }
  | {
      transport: 's3_multipart';
      upload_id: string;
      key: string;
    };

// One uploaded multipart part, in the snake_case shape the Rails endpoints speak.
// The upload hook maps these to and from Uppy's PartNumber/ETag/Size shape.
export type VideoUploadPart = {
  part_number: number;
  etag: string;
  size?: number;
};

// The three fields the upload-authorization request is derived from. The rest of
// the file stays in the browser; only these describe it to the backend.
export type VideoUploadFileMeta = {
  filename: string;
  content_type: string;
  byte_size: number;
};
// Host networking for the shared composer. Uppy owns the byte transfer.
export interface VideoUploadAdapter {
  request: (
    file: VideoUploadFileMeta,
    options: { multipart: boolean; signal?: AbortSignal },
  ) => Promise<{ video: VideoAsset; direct_upload: VideoAssetDirectUpload } | null>;
  signPart: (
    videoId: string,
    options: { upload_id: string; part_number: number; signal?: AbortSignal },
  ) => Promise<{ url: string } | null>;
  listParts: (
    videoId: string,
    options: { upload_id: string; signal?: AbortSignal },
  ) => Promise<{ parts: VideoUploadPart[] } | null>;
  complete: (
    videoId: string,
    options: { upload_id: string; parts: VideoUploadPart[]; signal?: AbortSignal },
  ) => Promise<{ location?: string | null } | null>;
  abort: (videoId: string, options: { upload_id: string; signal?: AbortSignal }) => Promise<void>;
  confirm: (videoId: string) => Promise<{ video: VideoAsset } | null>;
}
// ****************************************************
// END OF VIDEO ASSET TYPES
// ****************************************************

// ****************************************************
// START OF COMMUNITY VIDEO TYPES
// ****************************************************
// Updates pushed over a video's cable channel, told apart by type.
// status: revision is monotonic per backend save, so a client keeps the newest and
// drops out-of-order or stale messages. retryable is false when the backend would
// ignore a retry, such as a file MediaConvert cannot read.
// Why the backend rejected a failed video's source before transcoding it.
export type VideoFailureReason = 'too_long' | 'resolution_too_high' | 'unreadable';

// Only on surfaces that require approval: scanning, in_review until staff approve it, or approved.
export type VideoApprovalStatus = 'scanning' | 'in_review' | 'approved';

export type VideoStatusSnapshot = {
  type: 'status';
  status: VideoAssetStatus;
  revision: number;
  retryable?: boolean;
  reason?: VideoFailureReason;
  approvalStatus?: VideoApprovalStatus;
};

// thumbnails: generation finished, with or without candidates, so they can be loaded.
export type VideoThumbnailsUpdate = {
  type: 'thumbnails';
};

export type VideoUpdate = VideoStatusSnapshot | VideoThumbnailsUpdate;

// A short-lived, origin-bound Cloudflare URL for one ready video's HLS package,
// plus the video's selected thumbnail. The player renews it on a fixed interval
// before it expires.
export type VideoPlaybackAuthorization = {
  url: string;
  posterUrl?: string | null;
};

// One generated thumbnail candidate. Both URLs are short-lived; previewUrl is a
// small copy for the picker's tiles.
export type VideoThumbnailCandidate = {
  index: number;
  url: string;
  previewUrl: string;
};

// A thumbnail candidate as the API sends it.
export type VideoThumbnailCandidatePayload = {
  index: number;
  url: string;
  preview_url: string;
};

// The owner's thumbnail state for one video. candidates is null while generation
// is still running and empty when it produced none.
export type VideoThumbnails = {
  selectedUrl: string | null;
  selectedIndex: number | null;
  candidates: VideoThumbnailCandidate[] | null;
};

// Resolver result while the video is still transcoding, so the player waits
// instead of showing it as unavailable.
export const VIDEO_PLAYBACK_PROCESSING = 'processing' as const;

// Host-supplied resolver. The host closes over the post the video is bound to,
// so it takes only the video id and returns a fresh authorization,
// VIDEO_PLAYBACK_PROCESSING while the video is still processing, or null when
// playback is unavailable (unauthorized, failed, or gone).
export type VideoPlaybackResolver = (
  videoId: string,
) => Promise<VideoPlaybackAuthorization | typeof VIDEO_PLAYBACK_PROCESSING | null>;

// Host-supplied subscription to a video's status updates. Returns an unsubscribe.
export type VideoStatusSubscriber = (videoId: string, onUpdate: (update: VideoUpdate) => void) => () => void;
// ****************************************************
// END OF COMMUNITY VIDEO TYPES
// ****************************************************
